import type { GroupEvent, MessageDTO, ReplyPreviewDTO } from '@campus/core';
import { useMembersStore, sessionInfo } from '../people/membersStore.js';

/** Heure si aujourd'hui, « Hier », sinon la date courte. */
export function timeLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Hier';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export function dayLabel(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (date.toDateString() === yesterday.toDateString()) return 'Hier';
  return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}

export function hourLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/** « A », « A et B », « A, B et C ». */
function joinNames(names: string[]): string {
  return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} et ${names[names.length - 1]}`;
}

/**
 * Phrase d'un message système de groupe (« Inès a ajouté Paul »). Noms de
 * l'annuaire, sinon ceux gardés dans le message. Jamais d'accord selon le
 * genre : « t'a fait entrer », « t'a donné les droits d'admin ».
 */
export function systemText(m: Pick<MessageDTO, 'senderId' | 'senderName'>, event: GroupEvent): string {
  const me = sessionInfo().userId;
  const members = useMembersStore.getState().members;
  const nameOf = (id: string, fallback?: string) => members.get(id)?.displayName || fallback || 'Membre';
  const byMe = m.senderId === me;
  const actor = nameOf(m.senderId, m.senderName);
  const targets = 'userIds' in event ? event.userIds : [];
  const kept = 'names' in event ? event.names : [];
  const others = targets.flatMap((id, i) => (id === me ? [] : [nameOf(id, kept[i])]));
  const meToo = targets.includes(me);
  const list = joinNames(meToo ? [...others, 'toi'] : others);
  switch (event.type) {
    case 'created': return byMe ? `Tu as créé le groupe « ${event.title} »` : `${actor} a créé le groupe « ${event.title} »`;
    case 'renamed': return byMe ? `Tu as renommé le groupe « ${event.title} »` : `${actor} a renommé le groupe « ${event.title} »`;
    case 'left': return byMe ? 'Tu as quitté le groupe' : `${actor} a quitté le groupe`;
    case 'added':
      if (byMe) return `Tu as ajouté ${joinNames(others)}`;
      return meToo && others.length === 0 ? `${actor} t'a fait entrer dans le groupe` : `${actor} a ajouté ${list}`;
    case 'removed':
      if (byMe) return `Tu as retiré ${joinNames(others)} du groupe`;
      return meToo ? `${actor} t'a fait sortir du groupe` : `${actor} a retiré ${list} du groupe`;
    case 'admin-added':
      if (byMe) return `Tu as donné les droits d'admin à ${joinNames(others)}`;
      return meToo ? `${actor} t'a donné les droits d'admin du groupe` : `${actor} a donné les droits d'admin à ${list}`;
    case 'admin-removed':
      if (byMe) return meToo ? "Tu n'as plus les droits d'admin du groupe" : `Tu as retiré les droits d'admin à ${joinNames(others)}`;
      return meToo ? `${actor} t'a retiré les droits d'admin du groupe` : `${actor} a retiré les droits d'admin à ${list}`;
    case 'promoted':
      return meToo ? "Tu as maintenant les droits d'admin du groupe" : `${list} a maintenant les droits d'admin du groupe`;
  }
}

/** Aperçu d'un message dans la liste des conversations. */
export function previewText(m: MessageDTO): string {
  if (m.system) return systemText(m, m.system);
  if (m.deletedAt) return 'Message supprimé';
  if (m.text) return m.text.replace(/\s+/g, ' ');
  const files = m.attachments ?? [];
  const images = files.filter((a) => a.kind === 'image').length;
  if (files.length === 0) return '';
  if (images === files.length) return images === 1 ? 'Photo' : `${images} photos`;
  if (files.length > 1) return `${files.length} fichiers`;
  const only = files[0]!;
  if (only.kind === 'video') return 'Vidéo';
  if (only.kind === 'voice') return `Message vocal${only.durationMs ? ` ${formatDuration(only.durationMs)}` : ''}`;
  if (only.kind === 'audio') return `Son${only.durationMs ? ` ${formatDuration(only.durationMs)}` : ''}`;
  return `Fichier : ${only.name}`;
}

/** Extrait d'un message cité (au-dessus d'une réponse, bandeau « Répondre à »). */
export function quoteText(r: ReplyPreviewDTO): string {
  if (r.deleted) return 'Message supprimé';
  if (r.text) return r.text.replace(/\s+/g, ' ');
  const a = r.attachment;
  if (!a) return '';
  if (a.count > 1) return a.kind === 'image' ? `${a.count} photos` : `${a.count} fichiers`;
  if (a.kind === 'image') return 'Photo';
  if (a.kind === 'video') return 'Vidéo';
  if (a.kind === 'voice') return `Message vocal${a.durationMs ? ` ${formatDuration(a.durationMs)}` : ''}`;
  if (a.kind === 'audio') return `Son${a.durationMs ? ` ${formatDuration(a.durationMs)}` : ''}`;
  return 'Fichier';
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  const mb = bytes / (1024 * 1024);
  return `${mb < 10 ? mb.toFixed(1).replace('.', ',') : Math.round(mb)} Mo`;
}

export function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

/** Fichier refusé ou pas envoyé, en clair. */
export function uploadErrorLabel(code: string | undefined): string {
  switch (code) {
    case 'UNSUPPORTED_FILE': return 'Type de fichier non accepté.';
    case 'FILE_TOO_LARGE': return 'Trop lourd (photo 20 Mo, vidéo 200 Mo, son et document 50 Mo).';
    case 'QUOTA_EXCEEDED': return 'Plus de place pour les fichiers de l’espace. Préviens un admin.';
    case 'TOO_MANY_FILES': return 'Trop de fichiers envoyés cette heure-ci.';
    case 'FILE_UNREADABLE': return 'Fichier illisible ou abîmé.';
    case 'VOICE_TOO_LONG': return 'Message vocal trop long (5 min au plus).';
    case 'GUEST_FORBIDDEN': return 'Les invités n’envoient pas de fichiers.';
    case 'NETWORK': return 'Pas envoyé (réseau).';
    default: return 'Pas envoyé.';
  }
}

/** Message d'erreur d'envoi, en clair. */
export function sendErrorLabel(code: string | undefined): string {
  switch (code) {
    case 'RATE_LIMITED': return 'Doucement : trop de messages d’un coup. Réessaie dans un instant.';
    case 'RECIPIENT_NOT_FOUND': return 'Cette personne ne fait plus partie du campus.';
    case 'DM_CONNECTION_REQUIRED': return 'Pour écrire à cette personne, demande d’abord à vous connecter (sa fiche, panneau Personnes).';
    case 'MESSAGE_TOO_LONG': return 'Message trop long (4 000 caractères au plus).';
    case 'CONVERSATION_NOT_FOUND': return 'Tu n’as plus accès à cette conversation.';
    case 'REPLY_NOT_FOUND': return 'Le message auquel tu réponds a été supprimé.';
    case 'INVALID_TITLE': return 'Donne un nom au groupe (60 caractères au plus).';
    case 'GROUP_FULL': return 'Un groupe compte 50 personnes au plus.';
    case 'NOT_GROUP_ADMIN': return 'Seuls les admins du groupe peuvent faire ça.';
    case 'NOT_A_MEMBER': return 'Cette personne ne fait plus partie du groupe.';
    case 'ATTACHMENT_NOT_FOUND': return 'Un fichier joint n’est plus disponible. Retire ce message et joins-le à nouveau.';
    case 'NETWORK': return 'Pas envoyé (réseau).';
    default: return 'Pas envoyé.';
  }
}
